import { clearAuthCache, getHeaders } from "./auth";
import { handleHttpError } from "./errorHandler";
import { showToast } from "./toastOptions";
import { clearUserSessionCache } from "./useUserSession";

/**
 * Generic fetch wrapper with headers/CSRF, credentials, optional 404 allow, and toast handling.
 * Supports simple retries via { retries, retryDelayMs }.
 *
 * A 401 on a normal request clears the session caches, shows one toast, and
 * sends the browser to /login. Auth probes pass redirectOn401: false.
 */

export const SESSION_EXPIRED_MESSAGE = "Session has expired. Please sign in again.";

let authRedirectStarted = false;

const redirectToLoginOnce = () => {
  if (window.location.pathname.startsWith("/login")) {
    return false;
  }
  if (authRedirectStarted) {
    return true;
  }
  authRedirectStarted = true;
  clearAuthCache();
  clearUserSessionCache();
  showToast("error", SESSION_EXPIRED_MESSAGE);
  window.location.href = "/login";
  return true;
};

const prepareRequestBody = (body, headers) => {
  const isFormData = body instanceof FormData;
  if (isFormData) {
    // Let the browser set Content-Type with the correct multipart boundary
    delete headers["Content-Type"];
    return body;
  }
  if (body !== undefined && body !== null) {
    headers["Content-Type"] = "application/json";
    return JSON.stringify(body);
  }
  return body;
};

const handleResponse = async (res, method, url, allow404, redirectOn401) => {
  if (!res.ok) {
    if (allow404 && res.status === 404) {
      return res;
    }
    if (res.status === 401 && redirectOn401) {
      redirectToLoginOnce();
      return new Promise(() => {}); // Infinite promise stops execution chain
    }
    await handleHttpError(res, `Request failed: ${method} ${url}`);
  }

  const contentType = res.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return await res.json();
  }
  return null;
};

const shouldRetry = (attempt, retries) => {
  return attempt < retries;
};

const waitForRetry = async (retryDelayMs) => {
  await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
};

const handleRetryError = (error, suppressErrorToast, method, url) => {
  if (!suppressErrorToast && !error?.authRedirected) {
    showToast("error", error.message || `Request failed: ${method} ${url}`);
  }
  throw error;
};

export async function apiRequest(
  url,
  {
    method = "GET",
    body,
    forceRefresh = false,
    suppressErrorToast = false,
    allow404 = false,
    redirectOn401 = true,
    retries = 0,
    retryDelayMs = 250,
  } = {}
) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const headers = getHeaders(forceRefresh);
      const requestBody = prepareRequestBody(body, headers);

      const res = await fetch(url, {
        method,
        headers,
        credentials: "include",
        body: requestBody,
      });

      return await handleResponse(res, method, url, allow404, redirectOn401);
    } catch (error) {
      if (shouldRetry(attempt, retries)) {
        await waitForRetry(retryDelayMs);
        attempt += 1;
        continue;
      }
      handleRetryError(error, suppressErrorToast, method, url);
    }
  }
}
